/**
 * ExportListModal - Export list cards to various formats
 *
 * Formats, options and file metadata all come from `listRegistry`.
 */
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FaDownload, FaFileAlt, FaTable } from 'react-icons/fa';
import { IoDownload } from 'react-icons/io5';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import ExportFormatButtons from '@/features/import-export/components/ExportFormatButtons';
import ExportOptionsCheckboxes, { type ExportOptionConfig } from '@/features/import-export/components/ExportOptionsCheckboxes';
import ExportPreview from '@/features/import-export/components/ExportPreview';
import {
  LIST_EXPORT_FORMATS,
  exportList,
  getListExportDefaults,
  getListExportFormat,
  type ListExportFormat,
  type ListExportOptionsState,
} from '@/features/import-export/exporters/listRegistry';
import type { FormatOption } from '@/features/import-export/types';
import { fullCardCacheAtom } from '@/state/cardCache';
import type { CardList, EnrichedListCard } from '@/types/lists';

import { copyToClipboard, downloadAsFile } from '../utils/exporters';

// ============================================================================
// Types
// ============================================================================

export interface ExportListModalProps {
  isOpen: boolean;
  onClose: () => void;
  list: CardList;
  /** Every card on the list. */
  cards: EnrichedListCard[];
  /** Cards passing the current view filters; the scope control appears only when this is narrower. */
  filteredCards: EnrichedListCard[];
}

type ExportScope = 'all' | 'filtered';

// ============================================================================
// Constants
// ============================================================================

const FORMAT_ICONS: Record<ListExportFormat, ReactElement> = {
  mtgcraft: <FaDownload className="h-4 w-4" />,
  text: <FaFileAlt className="h-4 w-4" />,
  csv: <FaTable className="h-4 w-4" />,
};

const FORMAT_OPTIONS: FormatOption<ListExportFormat>[] = LIST_EXPORT_FORMATS.map((meta) => ({
  value: meta.value,
  label: meta.label,
  description: meta.description,
  icon: FORMAT_ICONS[meta.value],
}));

const OPTION_CONFIG: ExportOptionConfig<keyof ListExportOptionsState>[] = [
  { key: 'includeSetInfo', label: 'Include set & collector number' },
];

const DEFAULT_FORMAT: ListExportFormat = 'text';

const SCOPE_OPTIONS: { value: ExportScope; label: string }[] = [
  { value: 'filtered', label: 'Filtered' },
  { value: 'all', label: 'All' },
];

// ============================================================================
// Component
// ============================================================================

export default function ExportListModal({ isOpen, onClose, list, cards, filteredCards }: ExportListModalProps): ReactElement {
  const [format, setFormat] = useState<ListExportFormat>(DEFAULT_FORMAT);
  const [options, setOptions] = useState<ListExportOptionsState>(() => getListExportDefaults(DEFAULT_FORMAT));
  const [scope, setScope] = useState<ExportScope>('filtered');
  const fullCardCache = useAtomValue(fullCardCacheAtom);

  const formatMeta = getListExportFormat(format);

  const isFiltered = filteredCards.length !== cards.length;
  const exportedCards = isFiltered && scope === 'filtered' ? filteredCards : cards;

  // Reset options to the newly selected format's defaults so per-format
  // preferences don't leak across formats.
  const handleFormatChange = useCallback((next: ListExportFormat) => {
    setFormat(next);
    setOptions(getListExportDefaults(next));
  }, []);

  const exportContent = useMemo(
    () => (exportedCards.length === 0 ? '' : exportList(format, { list, cards: exportedCards, options, fullCardCache })),
    [format, list, exportedCards, options, fullCardCache],
  );

  const handleClose = useCallback(() => {
    setFormat(DEFAULT_FORMAT);
    setOptions(getListExportDefaults(DEFAULT_FORMAT));
    setScope('filtered');
    onClose();
  }, [onClose]);

  const handleCopy = useCallback(async () => copyToClipboard(exportContent), [exportContent]);

  const handleDownload = useCallback(() => {
    const safeName = list.name.replace(/[^a-zA-Z0-9 ]/g, '').trim();
    const timestamp = new Date().toISOString().split('T')[0];
    downloadAsFile(exportContent, `${safeName} - ${formatMeta.fileLabel} - ${timestamp}.${formatMeta.extension}`, formatMeta.mimeType);
  }, [exportContent, list.name, formatMeta]);

  if (cards.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Export List" size="md">
        <div className="py-8 text-center">
          <div className="bg-slate mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <IoDownload className="text-ash h-8 w-8" />
          </div>
          <p className="text-silver">No cards to export</p>
          <p className="text-ash mt-1 text-sm">Add some cards to this list first</p>
          <div className="mt-6">
            <Button text="Close" variant="outlined" onClick={handleClose} />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Export List" size="lg">
      <div className="space-y-5">
        <ExportFormatButtons options={FORMAT_OPTIONS} value={format} onChange={handleFormatChange} columns={3} />

        {isFiltered && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Cards:</span>
            <div className="inline-flex rounded-md border border-zinc-700 bg-zinc-800/50 p-0.5">
              {SCOPE_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setScope(value)}
                  aria-pressed={scope === value}
                  className={clsx(
                    'focus-visible:ring-aether rounded px-2.5 py-1 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2',
                    scope === value ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200',
                  )}
                >
                  {label} ({value === 'all' ? cards.length : filteredCards.length})
                </button>
              ))}
            </div>
          </div>
        )}

        <ExportOptionsCheckboxes
          config={OPTION_CONFIG}
          supportedOptions={formatMeta.supportedOptions}
          values={options}
          onChange={(key, value) => setOptions((prev) => ({ ...prev, [key]: value }))}
        />

        <ExportPreview content={exportContent} onCopy={handleCopy} onDownload={handleDownload} />

        <div className="flex justify-end pt-2">
          <button type="button" onClick={handleClose} className="text-ash hover:text-silver px-4 py-2 text-sm transition-colors">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
